import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, subirArchivo, downloadFile } from "@/lib/api";
import { Spinner } from "@/components/common";
import { AlertTriangle, RotateCcw, UploadCloud, History, X } from "lucide-react";

const fecha = (s) => (s ? new Date(s).toLocaleString("es-CO", { dateStyle: "short", timeStyle: "short" }) : "");

export function MotivosCorreccion({ c, compact }) {
  if (!c) return null;
  return (
    <div className={`w-full rounded-lg border border-red-200 bg-red-50 ${compact ? "p-2" : "p-3"}`} data-testid="correccion-motivos">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-red-700"><AlertTriangle className="h-3.5 w-3.5" /> HUMAN te pide volver a cargar este documento</p>
      <ul className="mt-1 list-disc pl-5 text-xs text-red-700">{(c.motivos_texto || []).map((m) => <li key={m}>{m}</li>)}</ul>
      {c.observacion && <p className="mt-1 text-xs text-red-800"><strong>Observaciones:</strong> {c.observacion}</p>}
      <p className="mt-1 text-[11px] text-red-500">Solicitado el {fecha(c.fecha)}</p>
    </div>
  );
}

export function VolverACargarBtn({ doc, onDone, small }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const subir = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await subirArchivo(`/portal/documentos/${doc.id}/recargar`, fd);
      toast.success("Documento cargado nuevamente. Queda pendiente de revisión por HUMAN.");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`doc-volver-cargar-${doc.id}`}
        className={`flex items-center gap-1.5 rounded-lg bg-vino-700 font-semibold text-white hover:bg-vino-800 disabled:opacity-60 ${small ? "px-2 py-1 text-[11px]" : "px-3 py-1.5 text-xs"}`}>
        {busy ? <Spinner /> : <UploadCloud className="h-3.5 w-3.5" />} Volver a cargar
      </button>
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden" onChange={(e) => { subir(e.target.files[0]); e.target.value = ""; }} />
    </>
  );
}

function RepetirModal({ eid, doc, onClose, onDone }) {
  const [motivos, setMotivos] = useState(null);
  const [sel, setSel] = useState([]);
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/documentos/motivos-repetir").then((r) => setMotivos(r.data)).catch(() => setMotivos([])); }, []);
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const enviar = async () => {
    setBusy(true);
    try {
      await api.post(`/expedientes/${eid}/documentos/${doc.id}/repetir-carga`, { motivos: sel, observacion: obs });
      toast.success("Solicitud enviada. El empleado verá los motivos en su portal.");
      onDone(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const faltaObs = sel.includes("otro") && obs.trim().length < 3;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" data-testid="repetir-carga-modal">
        <div className="mb-3 flex items-center justify-between"><p className="font-semibold text-slate-800">Solicitar repetir carga</p><button onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100" data-testid="repetir-cerrar"><X className="h-5 w-5" /></button></div>
        <p className="mb-3 text-sm text-slate-500">{doc.categoria} · {doc.original_filename}</p>
        {!motivos ? <Spinner /> : (
          <div className="space-y-2">
            {motivos.map((m) => (
              <label key={m.clave} className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50">
                <input type="checkbox" checked={sel.includes(m.clave)} onChange={() => toggle(m.clave)} data-testid={`repetir-motivo-${m.clave}`} className="accent-vino-700" />
                {m.label}{m.clave === "otro" && " (especificar en la observación)"}
              </label>
            ))}
          </div>
        )}
        <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={3} placeholder="Comentarios adicionales para el empleado" data-testid="repetir-observacion"
          className={`mt-3 w-full rounded-lg border px-3 py-2 text-sm outline-none ${faltaObs ? "border-red-300" : "border-slate-200"}`} />
        <button onClick={enviar} disabled={busy || !sel.length || faltaObs} data-testid="repetir-confirmar"
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-vino-700 px-4 py-2 text-sm font-semibold text-white hover:bg-vino-800 disabled:opacity-50">
          {busy ? <Spinner /> : <RotateCcw className="h-4 w-4" />} Confirmar solicitud
        </button>
      </div>
    </div>
  );
}

export function RepetirCargaBtn({ eid, doc, onDone }) {
  const [open, setOpen] = useState(false);
  if (doc.estado === "correccion_requerida") return null;
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid={`repetir-carga-${doc.id}`} className="flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50">
        <RotateCcw className="h-3.5 w-3.5" /> Solicitar repetir carga
      </button>
      {open && <RepetirModal eid={eid} doc={doc} onClose={() => setOpen(false)} onDone={onDone} />}
    </>
  );
}

export function HistorialCorrecciones({ eid, doc }) {
  const [open, setOpen] = useState(false);
  const corrs = doc.correcciones || [];
  if (!corrs.length) return null;
  return (
    <div className="w-full">
      <button onClick={() => setOpen(!open)} data-testid={`historial-correcciones-${doc.id}`} className="flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-vino-700">
        <History className="h-3 w-3" /> {corrs.length} solicitud{corrs.length > 1 ? "es" : ""} de corrección · versión actual v{doc.version || 1}
      </button>
      {open && (
        <div className="mt-2 space-y-2 rounded-lg bg-slate-50 p-2 text-[11px] text-slate-600">
          {corrs.map((c, i) => (
            <div key={c.id} className="border-l-2 border-red-200 pl-2">
              <p><strong>#{i + 1}</strong> {fecha(c.fecha)} · {c.solicitado_por_nombre || c.solicitado_por} · revisó v{c.version_revisada}</p>
              <p>{(c.motivos_texto || []).join(", ")}{c.observacion ? ` — ${c.observacion}` : ""}</p>
              <p className="text-slate-400">{c.respondida_at ? `Recargado ${fecha(c.respondida_at)} (v${c.version_cargada})` : "Pendiente de nueva carga"}</p>
            </div>
          ))}
          {(doc.versiones || []).map((v) => (
            <button type="button" key={v.version} onClick={() => downloadFile(`/expedientes/${eid}/documentos/${doc.id}/versiones/${v.version}/download`, v.original_filename || `version-${v.version}`)}
              data-testid={`version-anterior-${doc.id}-${v.version}`} className="block text-left text-vino-700 hover:underline">Ver versión anterior v{v.version} · {v.original_filename}</button>
          ))}
        </div>
      )}
    </div>
  );
}
